import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseIcon, EmployeesIconActive } from "@components/Icons";
import { useAuth } from "@context/auth/AuthContext";
import { maskCNPJ } from "@utils/index";
import Button from "@components/Button";
import { CNPJS } from "@context/auth/IAuthContext";
import { useMyDriversContext } from "@context/MyDriversContext";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import { IMyDriversService } from "@modules/user/models/IMyDriversService";
import { Types } from "@ioc/types";
import useDialogAlert from "@hooks/useDialogAlert";
import AppError from "@utils/AppError";
import { Drivers } from "../models";
import { linkHeaders, tabsLinkModal } from "../constants";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  data: {
    id: string;
    name: string;
    companies: Drivers["companies"];
  };
}

const Rows = React.memo<{
  companyName: string;
  CNPJ: string;
  BpID: string;
  data: Props["data"];
  listCompany: { companyDocuments: string[] };
  setListCompany: React.Dispatch<
    React.SetStateAction<{ companyDocuments: string[] }>
  >;
  setDisablePerTransition: React.Dispatch<React.SetStateAction<boolean>>;
}>(
  ({
    companyName,
    BpID,
    data,
    CNPJ,
    listCompany,
    setListCompany,
    setDisablePerTransition,
  }) => {
    const dataList = [companyName, maskCNPJ(CNPJ)];

    const handleMountCNPJList = (CNPJ: string) => {
      setDisablePerTransition(false);

      const { companyDocuments } = listCompany;
      const isCNPJIncluded = companyDocuments.includes(CNPJ);

      if (isCNPJIncluded) {
        const copy = [...companyDocuments];
        const filtered = copy.filter((item) => item !== CNPJ);
        setListCompany((prevState) => ({
          ...prevState,
          companyDocuments: filtered,
        }));
      } else {
        const updatedDocuments = [...companyDocuments, CNPJ];

        setListCompany((prevState) => ({
          ...prevState,
          companyDocuments: updatedDocuments,
        }));
      }
    };

    return (
      <TableRow key={`${BpID}`}>
        <TableCell>
          {dataList.map((item) => (
            <Typography>{item}</Typography>
          ))}
        </TableCell>

        <TableCell>
          <Typography>
            {data.companies.find((items) => items.CNPJ === CNPJ)
              ?.lastSchedule ?? "-"}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <Checkbox
            onChange={() => handleMountCNPJList(CNPJ)}
            checked={listCompany.companyDocuments.includes(CNPJ)}
          />
        </TableCell>
      </TableRow>
    );
  }
);

const LinkModal: React.FC<Props> = ({ open, onClose, data }) => {
  const [tab, setTab] = useState<number>(0);
  const [list, setList] = useState<CNPJS[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [mediator, setMediator] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [disablePerTransition, setDisablePerTransition] = useState<boolean>(
    true
  );

  const theme = useTheme();
  const styles = useStyles(theme);

  const { listCNPJ } = useAuth();
  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIoCContext();
  const {
    listCompany,
    setListCompany,
    fetchAllDrivers,
  } = useMyDriversContext();

  const myDriversService = serviceContainer.get<IMyDriversService>(
    Types.Users.IMyDriverService
  );

  const filterByTab = useMemo(
    () =>
      listCNPJ.filter((items) =>
        tab
          ? data.companies.some((item) => item.CNPJ === items.CNPJ)
          : !data.companies.some((item) => item.CNPJ === items.CNPJ)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab]
  );

  const handleSearchTerm = useCallback(() => {
    const numericTerm = searchTerm.replace(/\D/g, "");
    const isCNPJ = /^\d+$/.test(numericTerm);

    const dataFiltered = filterByTab.filter((item) =>
      item[isCNPJ ? "CNPJ" : "companyName"]
        .toLowerCase()
        .startsWith(isCNPJ ? numericTerm : searchTerm.toLowerCase())
    );

    setList(dataFiltered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, listCNPJ]);

  const handleClose = () => {
    setListCompany({ companyDocuments: [] });
    onClose();
  };

  const handleEnableButton = useMemo(() => {
    if (!tab) {
      return listCompany.companyDocuments.every((item) =>
        mediator.includes(item)
      );
    } else {
      return listCompany.companyDocuments.length >= mediator.length;
    }
  }, [listCompany.companyDocuments, mediator, tab]);

  const responsePhrases = {
    success: `Cliente(s) ${tab ? "excluído(s)" : "incluído(s)"} com sucesso!`,
    error: `Não foi possível ${tab ? "excluir" : "incluir"} o(s) cliente(s)`,
  };

  const handleUpdateDriverAssociations = useCallback(async () => {
    try {
      setLoading(true);
      await myDriversService.updateDriverAssociations(data.id, listCompany);

      snackbar({
        message: responsePhrases.success,
        variant: "success",
        autoHideDuration: 3000,
      });

      handleClose();
      fetchAllDrivers();
    } catch (error) {
      if (error instanceof AppError) {
        snackbar({
          message: `${responsePhrases.error}: ${error.message}`,
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id, data.name, listCompany]);

  useEffect(() => {
    handleSearchTerm();
  }, [searchTerm, handleSearchTerm]);

  useEffect(() => {
    setMediator(listCompany.companyDocuments);
    setSearchTerm("");
    setDisablePerTransition(true);

    setListCompany((prevState) => ({
      ...prevState,
      companyDocuments: data.companies.map((item) => item.CNPJ),
    }));

    setList(filterByTab);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <Modal open={open} onClose={handleClose} sx={styles.root}>
      <Card sx={styles.card}>
        <Stack sx={styles.header}>
          <Typography sx={styles.title}>
            Atualize os vínculos que o motorista possui
          </Typography>

          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack sx={styles.user_content}>
          <Box sx={styles.icon}>
            <EmployeesIconActive />
          </Box>
          <Typography sx={styles.title}>{data.name}</Typography>
        </Stack>

        <TextField
          placeholder="Nome ou CNPJ"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={({ target: { value } }) => setSearchTerm(value)}
        />

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabsLinkModal.map((item) => (
            <Tab sx={{ textTransform: "capitalize" }} label={item.label} />
          ))}
        </Tabs>

        {!list.length ? (
          <Typography sx={{ textAlign: "center", padding: "2rem" }}>
            Não há nada por aqui
          </Typography>
        ) : (
          <TableContainer sx={styles.table_container}>
            <Table>
              <TableHead sx={styles.table_head}>
                {linkHeaders.map((item, index) => (
                  <TableCell
                    key={`${item.id}-${index + 1}`}
                    sx={styles.table_head_cells}
                  >
                    {item.label}
                  </TableCell>
                ))}
              </TableHead>

              <TableBody>
                {list.map((item) => (
                  <Rows
                    key={item.BpID}
                    data={data}
                    companyName={item.companyName}
                    CNPJ={item.CNPJ}
                    BpID={item.BpID as string}
                    listCompany={listCompany}
                    setListCompany={setListCompany}
                    setDisablePerTransition={setDisablePerTransition}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack sx={styles.buttons_content}>
          <Button
            style={{ color: theme.palette.grey[700] }}
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            style={{
              backgroundColor: !tab ? theme.palette.success.light : undefined,
            }}
            disabled={handleEnableButton || disablePerTransition}
            startIcon={
              loading && <CircularProgress sx={{ color: "#FFF" }} size="2rem" />
            }
            variant="contained"
            onClick={() => handleUpdateDriverAssociations()}
          >
            {tab ? "Excluir" : "Incluir"}
          </Button>
        </Stack>
      </Card>
    </Modal>
  );
};

const useStyles = (theme: Theme) => {
  const styles = {
    root: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    } as CSSProperties,
    card: {
      padding: "2rem",
      display: "flex",
      flexDirection: "column",
      gap: "1.5rem",
      width: "60rem",
    } as CSSProperties,
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    } as CSSProperties,
    title: { fontWeight: 600, color: theme.palette.grey[800] } as CSSProperties,
    user_content: {
      flexDirection: "row",
      gap: "1rem",
      alignItems: "center",
    } as CSSProperties,
    icon: {
      backgroundColor: "#FFF5F7",
      height: "5rem",
      width: "5rem",
      borderRadius: "50%",
      display: "flex",
      padding: "1rem",
      alignItems: "center",
      justifyContent: "center",
    } as CSSProperties,
    table_container: { maxHeight: "30rem", minWidth: "50rem" } as CSSProperties,
    table_head: {
      position: "sticky",
      top: 0,
      backgroundColor: "#FFF",
      zIndex: 999,
    } as CSSProperties,
    table_head_cells: {
      fontSize: "1.3rem",
      fontWeight: 600,
      color: theme.palette.grey[800],
    } as CSSProperties,
    buttons_content: {
      flexDirection: "row",
      alignSelf: "flex-end",
      gap: "1rem",
    } as CSSProperties,
  };

  return styles;
};

export { LinkModal };
